import { type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface ConfirmModalProps {
  open: boolean;
  title: string;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
}: ConfirmModalProps) {
  if (!open) return null;

  const confirmBg = variant === 'danger'
    ? 'var(--color-action-danger)'
    : 'var(--color-action-primary)';
  const confirmHover = variant === 'danger'
    ? 'var(--color-action-danger-hover)'
    : 'var(--color-action-primary-hover)';

  return createPortal(
    <>
      {/* Overlay */}
      <div
        aria-hidden="true"
        onClick={onCancel}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'var(--modal-overlay-bg)',
          zIndex: 'var(--z-overlay)',
        }}
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 'var(--z-modal)',
          width: '100%',
          maxWidth: 'var(--modal-max-width)',
          backgroundColor: 'var(--color-surface-white)',
          borderRadius: 'var(--modal-radius)',
          boxShadow: 'var(--modal-shadow)',
          padding: 'var(--modal-padding)',
        }}
      >
        {/* Icon */}
        {variant === 'danger' && (
          <div style={{
            width: 'var(--space-10)',
            height: 'var(--space-10)',
            borderRadius: 'var(--radius-full)',
            backgroundColor: 'var(--color-danger-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 'var(--space-4)',
            color: 'var(--color-danger)',
          }}>
            <IconTrash />
          </div>
        )}

        {/* Title */}
        <h2
          id="confirm-modal-title"
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xl)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--color-text-primary)',
            marginBottom: description ? 'var(--space-2)' : 'var(--space-6)',
            lineHeight: 'var(--leading-tight)',
          }}
        >
          {title}
        </h2>

        {/* Description */}
        {description && (
          <p style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            color: 'var(--color-text-muted)',
            lineHeight: 'var(--leading-normal)',
            marginBottom: 'var(--space-6)',
          }}>
            {description}
          </p>
        )}

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: 'var(--space-3)',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onCancel}
            style={{
              height: 'var(--btn-height)',
              padding: '0 var(--btn-padding-x)',
              borderRadius: 'var(--btn-radius)',
              border: '1px solid var(--color-action-secondary-border)',
              backgroundColor: 'var(--color-action-secondary)',
              color: 'var(--color-action-secondary-text)',
              fontSize: 'var(--btn-font-size)',
              fontWeight: 'var(--btn-font-weight)',
              fontFamily: 'var(--font-sans)',
              cursor: 'pointer',
              transition: 'background-color var(--transition-base)',
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--color-action-secondary-hover)')}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'var(--color-action-secondary)')}
          >
            {cancelLabel}
          </button>

          <button
            onClick={onConfirm}
            style={{
              height: 'var(--btn-height)',
              padding: '0 var(--btn-padding-x)',
              borderRadius: 'var(--btn-radius)',
              border: 'none',
              backgroundColor: confirmBg,
              color: 'var(--color-action-danger-text)',
              fontSize: 'var(--btn-font-size)',
              fontWeight: 'var(--btn-font-weight)',
              fontFamily: 'var(--font-sans)',
              cursor: 'pointer',
              transition: 'background-color var(--transition-base)',
            }}
            onMouseEnter={e => (e.currentTarget.style.backgroundColor = confirmHover)}
            onMouseLeave={e => (e.currentTarget.style.backgroundColor = confirmBg)}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </>,
    document.body
  );
}

function IconTrash() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
    </svg>
  );
}
